/** Secteurs Quillou — arborescence Google Drive (Envoi_CTS). */
export const SECTEURS = [
	{ id: 1, path: 'Quillou_ELITE', nom: 'ELITE', fichier: '—', date: '—', statut: 'Connexion requise' },
	{ id: 2, path: 'Quillou_LIGUE', nom: 'LIGUE', fichier: '—', date: '—', statut: 'Connexion requise' },
	{ id: 3, path: 'Quillou_AOV', nom: 'Aubrac Olt Viadène', fichier: '—', date: '—', statut: 'Connexion requise' },
	{ id: 4, path: 'Quillou_CEOR', nom: 'Céor Lagast', fichier: '—', date: '—', statut: 'Connexion requise' },
	{ id: 5, path: 'Quillou_LEV', nom: 'Lévézou', fichier: '—', date: '—', statut: 'Connexion requise' },
	{ id: 6, path: 'Quillou_PC', nom: 'Palange Comtal', fichier: '—', date: '—', statut: 'Connexion requise' },
	{ id: 7, path: 'Quillou_SEG', nom: 'Ségala', fichier: '—', date: '—', statut: 'Connexion requise' },
	{ id: 8, path: 'Quillou_VD', nom: 'Vallon Dourdou', fichier: '—', date: '—', statut: 'Connexion requise' },
	{ id: 9, path: 'Quillou_VETERANS', nom: 'VETERANS', fichier: '—', date: '—', statut: 'Connexion requise' },
];

function formatFileSize(bytes) {
	if (!bytes) return 'N/A';
	const units = ['B', 'KB', 'MB', 'GB'];
	let size = Number(bytes);
	let unitIndex = 0;
	while (size >= 1024 && unitIndex < units.length - 1) {
		size /= 1024;
		unitIndex += 1;
	}
	return `${size > 0 ? size.toFixed(2) : '0.0'} ${units[unitIndex]}`;
}

function formatModifiedDate(modifiedTime) {
	if (!modifiedTime) return 'N/A';
	return new Date(modifiedTime).toLocaleString('fr-FR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
}

async function fetchDrive(url, accessToken) {
	const response = await fetch(url, {
		headers: { Authorization: `Bearer ${accessToken}` },
	});
	if (response.status === 401) {
		const err = new Error('UNAUTH');
		err.unauth = true;
		throw err;
	}
	return response;
}

export const GOOGLE_TOKEN_KEY = 'Q8__verif_scores__googleAccessToken';
export const GOOGLE_EXPIRES_KEY = 'Q8__verif_scores__googleTokenExpiresAt';

export function clearGoogleDriveSession() {
	localStorage.removeItem(GOOGLE_TOKEN_KEY);
	localStorage.removeItem(GOOGLE_EXPIRES_KEY);
}

/** Jeton encore valide (avec marge 60 s) ou null */
export function readValidGoogleAccessToken() {
	const token = localStorage.getItem(GOOGLE_TOKEN_KEY);
	const expiresAtRaw = localStorage.getItem(GOOGLE_EXPIRES_KEY);
	if (!token && !expiresAtRaw) return null;
	if (token && !expiresAtRaw) {
		clearGoogleDriveSession();
		return null;
	}
	if (!token || !expiresAtRaw) return null;
	const expiresAt = Number(expiresAtRaw);
	if (!Number.isFinite(expiresAt) || Date.now() >= expiresAt - 60_000) {
		clearGoogleDriveSession();
		return null;
	}
	return token;
}

export function saveGoogleAccessToken(accessToken, expiresIn) {
	const ttlSec = typeof expiresIn === 'number' && expiresIn > 0 ? expiresIn : 3600;
	const expiresAt = Date.now() + ttlSec * 1000;
	localStorage.setItem(GOOGLE_TOKEN_KEY, accessToken);
	localStorage.setItem(GOOGLE_EXPIRES_KEY, String(expiresAt));
}

export async function listDriveFolders(accessToken) {
	const response = await fetchDrive(
		"https://www.googleapis.com/drive/v3/files?q=mimeType='application/vnd.google-apps.folder'&fields=files(id,name,modifiedTime)",
		accessToken,
	);
	if (!response.ok) throw new Error('Erreur récupération des dossiers');
	const data = await response.json();
	return data.files ?? [];
}

async function listEnvoiCtsFiles(parentFolderId, accessToken) {
	const envoiFolderRes = await fetchDrive(
		`https://www.googleapis.com/drive/v3/files?q='${parentFolderId}' in parents and mimeType='application/vnd.google-apps.folder' and name='Envoi_CTS'&fields=files(id,name)`,
		accessToken,
	);
	if (!envoiFolderRes.ok) throw new Error('Erreur récupération du dossier Envoi_CTS');

	const envoiFolderData = await envoiFolderRes.json();
	const envoiFolder = envoiFolderData.files?.[0];
	if (!envoiFolder) return null;

	const filesRes = await fetchDrive(
		`https://www.googleapis.com/drive/v3/files?q='${envoiFolder.id}' in parents and trashed=false&fields=files(id,name,modifiedTime,mimeType,size)`,
		accessToken,
	);
	if (!filesRes.ok) throw new Error('Erreur récupération des fichiers');

	const filesData = await filesRes.json();
	const folderFiles = filesData.files ?? [];
	folderFiles.sort((a, b) => new Date(b.modifiedTime) - new Date(a.modifiedTime));
	return folderFiles;
}

/** Charge le statut Envoi_CTS pour chaque secteur Quillou. */
export async function loadSecteursFromDrive(accessToken, secteurs = SECTEURS) {
	const folders = await listDriveFolders(accessToken);

	const updatedSecteurs = await Promise.all(
		secteurs.map(async (secteur) => {
			const matchingFolder = folders.find((f) => f.name.includes(secteur.path));
			if (!matchingFolder) return secteur;

			const folderFiles = await listEnvoiCtsFiles(matchingFolder.id, accessToken);
			if (!folderFiles) {
				return {
					...secteur,
					fichier: 'Dossier Envoi_CTS non trouvé',
					taille: 'N/A',
					date: 'N/A',
					statut: 'Dossier manquant',
					files: [],
				};
			}

			return {
				...secteur,
				fichier: folderFiles.length > 0
					? folderFiles.map((f) => f.name).join(', ')
					: 'Aucun fichier',
				taille: folderFiles.length > 0
					? folderFiles.map((f) => formatFileSize(f.size)).join(', ')
					: 'N/A',
				date: formatModifiedDate(folderFiles[0]?.modifiedTime),
				statut: folderFiles.length > 0
					? `${folderFiles.length} fichier(s) à analyser`
					: 'Pas de fichier',
				files: folderFiles,
			};
		}),
	);

	return { folders, secteurs: updatedSecteurs };
}
